import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { type OptionType } from '@/api/common';
import CloseIcon from '@/assets/svg/bx-x.svg?react';
import { Button } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { Text } from '@/components/typography';
import { ChannelSelect } from '@/features/inputs';
import VendorSelect from '@/features/inputs/VendorSelect';

type Props = {
  disabledVendorIds: string[];
  onClose: () => void;
  onAddVendor: ({ channel, vendor }: { channel: OptionType; vendor: OptionType }) => void;
};

const VendorSpendAddVendor = ({ disabledVendorIds, onClose, onAddVendor }: Props) => {
  const formSchema = z.object({
    channel: z.custom<OptionType>((value) => value != null, t`Channel is Required`),
    vendor: z.custom<OptionType>((value) => value != null, t`Vendor is Required`),
  });

  const form = useAppForm({
    defaultValues: {
      channel: null as OptionType | null,
      vendor: null as OptionType | null,
    },
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: ({ value }) => {
      if (!value.channel || !value.vendor) {
        return;
      }
      onAddVendor({
        channel: value.channel,
        vendor: value.vendor,
      });
    },
  });

  return (
    <div className="relative flex w-full flex-col gap-4 rounded-xs border border-gray-300 bg-gray-300 p-8">
      <Text variant="headline" color="dark-grey" weight="light">
        <Trans>New Vendor</Trans>
      </Text>
      <form
        className="flex flex-col gap-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
      >
        <div className="flex flex-1 flex-wrap gap-x-9 gap-y-6">
          <form.AppField name="channel">
            {(field) => (
              <field.Field className="w-fit">
                <FieldLabel htmlFor="vendor-spend-channel">
                  <Trans>Channel</Trans>
                </FieldLabel>
                <ChannelSelect
                  id="vendor-spend-channel"
                  className="w-85"
                  value={field.state.value}
                  onValueChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
                <field.Error />
              </field.Field>
            )}
          </form.AppField>
          <form.Subscribe selector={(state) => state.values.channel}>
            {(channel) => (
              <form.AppField name="vendor">
                {(field) => (
                  <field.Field className="w-fit">
                    <FieldLabel htmlFor="vendor-spend-vendor">
                      <Trans>Vendor</Trans>
                    </FieldLabel>
                    <VendorSelect
                      id="vendor-spend-vendor"
                      className="w-85"
                      value={field.state.value}
                      onValueChange={field.handleChange}
                      onBlur={field.handleBlur}
                      disabledVendorIds={disabledVendorIds}
                      channelId={channel?.id}
                      disabled={!channel?.id}
                    />
                    <field.Error />
                  </field.Field>
                )}
              </form.AppField>
            )}
          </form.Subscribe>
        </div>
        <form.AppForm>
          <div className="mt-3 flex flex-nowrap gap-3.5">
            <Button type="button" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <form.SubmitButton>
              <Trans>Add</Trans>
            </form.SubmitButton>
          </div>
        </form.AppForm>
      </form>
      <Button
        aria-label={t`Close`}
        variant="ghost"
        className="absolute top-1 right-1"
        size="icon"
        onClick={onClose}
      >
        <CloseIcon />
      </Button>
    </div>
  );
};

export default VendorSpendAddVendor;
